import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Trans } from "@lingui/react"
import { getCurrency } from "../../view/shared/utilities/currency"
import styles from "./OfflineBalanceBanner.module.css"
import CustomModal from "./Modal"

const OfflineBalanceBanner = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { offlineState, onlineState, group } = useSelector((state) => ({
    offlineState: state.accountV2.offline,
    onlineState: state.accountV2.online,
    group: state.group,
  }))

  if (!offlineState) {
    return null // Don't render anything if there are no offline changes
  }

  const detailedChanges = []
  let totalIncome = 0
  let totalExpense = 0

  // Calculate the delta for all balances
  for (const key in offlineState?.balances) {
    const offlineBalance = offlineState.balances[key]?.balance || 0
    const onlineBalance = onlineState.balances[key]?.balance || 0
    const diff = offlineBalance - onlineBalance

    if (diff !== 0) {
      detailedChanges.push({
        accountType: key,
        change: diff,
      })
    }

    if (key === "savings") {
      if (diff > 0) {
        totalIncome += diff
      } else if (diff < 0) {
        totalExpense += Math.abs(diff)
      }
    }
  }

  if (totalIncome === 0 && totalExpense === 0) {
    return null // Nothing to show if the main fund hasn't changed
  }

  const currency = getCurrency(group?.country).symbol

  return (
    <>
      <div className={styles.banner}>
        <p className={styles.title}>
          <Trans id="offline_balance_title">Offline Balance Update</Trans>
        </p>
        <p className={styles.balanceInfo}>
          <Trans id="new_offline_balance">New Offline Balance</Trans>: {currency}
          {(offlineState.balances.savings?.balance || 0).toFixed(2)}
        </p>
        <button
          onClick={() => setIsExpanded(true)}
          className={styles.detailsButton}
        >
          <Trans id="details_button">Details</Trans>
        </button>
      </div>
      <CustomModal
        modalHeader={<Trans id="offline_balance_title">Offline Balance Update</Trans>}
        isOpen={isExpanded}
        toggle={() => setIsExpanded(!isExpanded)}
        bodyContent={
          <div>
            <p className={styles.balanceInfo}>
              <Trans id="current_saved_balance">Current Saved Balance</Trans>: {currency}
              {(onlineState.balances.savings?.balance || 0).toFixed(2)}
            </p>
            <p className={`${styles.balanceInfo} ${styles.income}`}>
              <Trans id="pending_income">Pending Income</Trans>: +{currency}
              {totalIncome.toFixed(2)}
            </p>
            <p className={`${styles.balanceInfo} ${styles.expense}`}>
              <Trans id="pending_expense">Pending Expense</Trans>: -{currency}
              {totalExpense.toFixed(2)}
            </p>
            <hr className={styles.divider} />
            <p className={styles.newBalance}>
              <Trans id="new_offline_balance">New Offline Balance</Trans>: {currency}
              {(offlineState.balances.savings?.balance || 0).toFixed(2)}
            </p>
            <div className={styles.detailsContainer}>
              {detailedChanges.map(({ accountType, change }) => (
                <p key={accountType} className={styles.detailItem}>
                  <strong>{accountType}:</strong> {change > 0 ? "+" : ""}
                  {currency}
                  {change.toFixed(2)}
                </p>
              ))}
            </div>
          </div>
        }
        footerContent={
          <button
            onClick={() => setIsExpanded(false)}
            className="btn btn-light"
          >
            <Trans id="close">Close</Trans>
          </button>
        }
      />
    </>
  )
}

export default OfflineBalanceBanner
